import { useCallback, useSyncExternalStore } from 'react';

// The one breakpoint the shell has to know in JavaScript (REQ-282 AC1, DESIGN_SYSTEM §10).
//
// Everything else about the responsive nav is CSS — the 232 px nav becomes a 64 px icon rail at 1180 px
// without changing a single element. The phone shape is different: below 768 px the side nav is replaced by
// a bottom tab bar, and rendering both and hiding one with CSS would put two `<nav aria-label="Main">`
// landmarks in the accessibility tree at once. So the shell renders exactly one of them, and this hook says
// which. `matchMedia` is the source, subscribed through `useSyncExternalStore`; where it does not exist
// (jsdom without `src/testing/viewport.ts`), the answer is "not a phone", which is the desktop shape.

export const PHONE_QUERY = '(max-width: 767px)';

function media(query: string): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query)
    : null;
}

export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = media(query);
      if (!list) return () => {};
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );
  const getSnapshot = useCallback(() => media(query)?.matches ?? false, [query]);
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/** True below 768 px: no side nav, a bottom tab bar instead (DESIGN_SYSTEM §10 breakpoints). */
export function usePhoneLayout(): boolean {
  return useMediaQuery(PHONE_QUERY);
}
